import { Link } from 'react-router'
import { siteConfig } from '../../config/site'
import {
  getPreferredServicePath,
  serviceCategories,
  servicePages,
  type ServicePageContentModel,
} from '../../data'
import { ServiceContent } from './ServiceContent'
import { ServiceCTA } from './ServiceCTA'
import { ServiceHero } from './ServiceHero'
import { StoreInfoPanel } from './StoreInfoPanel'

interface ServicePageProps {
  page: ServicePageContentModel
}

export function ServicePage({ page }: ServicePageProps) {
  const category = serviceCategories.find(({ id }) => id === page.parentCategory)
  const categoryServices = servicePages.filter(
    ({ categoryId, enabled }) => enabled && categoryId === page.parentCategory,
  )
  const partnerVariant = page.hero.partnerBrand?.variant
  const carrierId = partnerVariant === 'fedex' || partnerVariant === 'usps' || partnerVariant === 'dhl'
    ? partnerVariant
    : undefined

  return (
    <article className="service-page">
      <title>{page.seo.title}</title>
      <meta name="description" content={page.seo.description} />
      <link rel="canonical" href={`${siteConfig.siteUrl}${page.canonicalPath}`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteConfig.displayName} />
      <meta property="og:title" content={page.seo.openGraphTitle ?? page.seo.title} />
      <meta
        property="og:description"
        content={page.seo.openGraphDescription ?? page.seo.description}
      />
      <meta property="og:url" content={`${siteConfig.siteUrl}${page.canonicalPath}`} />
      <header className="service-page__title-strip">
        <div className="service-page__shell">{page.pageName}</div>
      </header>

      <div className="service-page__shell service-page__layout">
        <aside className="service-category-menu" aria-label={`${page.parentCategoryLabel} services`}>
          <h2>{page.parentCategoryLabel}</h2>
          <nav>
            <ul>
              {categoryServices.map((service) => {
                const href = getPreferredServicePath(
                  service.id,
                  `/services/${category?.slug ?? page.parentCategory}/${service.slug}`,
                )
                return (
                  <li key={service.id}>
                    <Link className={service.id === page.serviceId ? 'is-active' : undefined} to={href}>
                      {service.name}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </nav>
        </aside>

        <div className="service-page__main">
          <nav className="service-breadcrumbs" aria-label="Breadcrumb">
            <Link to={page.parentCategoryPath}>{page.parentCategoryLabel}</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">{page.pageName}</span>
          </nav>

          <ServiceHero hero={page.hero} pageName={page.pageName} />

          <div className="service-page__copy">
            <h1>{page.heading}</h1>
            <p className="service-page__subheading">{page.subheading}</p>
            <ServiceContent sections={page.sections} callout={page.callout} />
            <ServiceCTA
              question={page.question}
              showOnlinePrintCta={page.showOnlinePrintCta}
            />
            <StoreInfoPanel carrierId={carrierId} />
          </div>
        </div>
      </div>
    </article>
  )
}
